import { ArrowStyle } from "./ArrowStyle";

import { GeomUtil } from "../geom/GeomUtil";

import Graphics = createjs.Graphics;
import Point = createjs.Point;

/**
 *
 * Copyright (c) 2008 Noel Billig (www.dncompute.com)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

export class GraphicsUtil {

    public static BEZIER_LENGTH_TO_CURVE: number = .1;

    public static drawArrow(graphics: Graphics, start: Point, end: Point, style: any = null): void {

        if (start.equals(end)) {
            return;
        }

        let arrowStyle: ArrowStyle;

        if (style == null) {
            arrowStyle = new ArrowStyle();
        }
        else if (style instanceof ArrowStyle) {
            arrowStyle = (<ArrowStyle>style);
        }
        else {
            arrowStyle = new ArrowStyle(style);
        }

        let fullVect: Point = end.subtract(start);
        let halfWidth: number = (arrowStyle.headWidth != -1) ? arrowStyle.headWidth / 2 : arrowStyle.headLength / 2;

        //Figure out the line start/end points
        let startNorm: Point = new Point(fullVect.y, -fullVect.x);
        startNorm.normalize(arrowStyle.shaftThickness / 2);
        let start1: Point = start.add(startNorm);
        let start2: Point = start.subtract(startNorm);
        let end1: Point = end.add(startNorm);
        let end2: Point = end.subtract(startNorm);

        //figure out where the arrow head starts
        let headPnt: Point = fullVect.clone();
        headPnt.normalize(headPnt.length - arrowStyle.headLength);
        headPnt = headPnt.add(start);

        //calculate the arrowhead corners
        let headPntNorm: Point = startNorm.clone();
        headPntNorm.normalize(halfWidth);
        let edge1: Point = headPnt.add(headPntNorm);
        let edge2: Point = headPnt.subtract(headPntNorm);

        //Figure out where the arrow connects the the shaft, then calc the intersections
        let shaftCenter: Point = Point.interpolate(end, headPnt, arrowStyle.shaftPosition);
        let inter1: Point = GeomUtil.getLineIntersection(start1, end1, shaftCenter, edge1);
        let inter2: Point = GeomUtil.getLineIntersection(start2, end2, shaftCenter, edge2);

        //Figure out the control points
        let edgeCenter: Point = Point.interpolate(end, headPnt, arrowStyle.edgeControlPosition);
        let edgeNorm: Point = startNorm.clone();
        edgeNorm.normalize(halfWidth * arrowStyle.edgeControlSize);
        let edgeCntrl1: Point = edgeCenter.add(edgeNorm);
        let edgeCntrl2: Point = edgeCenter.subtract(edgeNorm);

        graphics.moveTo(start1.x, start1.y);
        graphics.lineTo(inter1.x, inter1.y);
        graphics.lineTo(edge1.x, edge1.y);
        graphics.curveTo(edgeCntrl1.x, edgeCntrl1.y, end.x, end.y);
        graphics.curveTo(edgeCntrl2.x, edgeCntrl2.y, edge2.x, edge2.y);
        graphics.lineTo(inter2.x, inter2.y);
        graphics.lineTo(start2.x, start2.y);
        graphics.lineTo(start1.x, start1.y);
    }

    public static drawArrowBezier(graphics: Graphics, start: Point, end: Point, style: any = null): createjs.Rectangle {

        if (start.equals(end)) {
            return;
        }

        let arrowStyle: ArrowStyle;

        if (style == null) {
            arrowStyle = new ArrowStyle();
        }
        else if (style instanceof ArrowStyle) {
            arrowStyle = (<ArrowStyle>style);
        }
        else {
            arrowStyle = new ArrowStyle(style);
        }

        let fullVect: Point = end.subtract(start);
        let halfWidth: number = (arrowStyle.headWidth != -1) ? arrowStyle.headWidth / 2 : arrowStyle.headLength / 2;

        //Figure out the line start/end points
        let startNorm: Point = new Point(fullVect.y, -fullVect.x);
        startNorm.normalize(arrowStyle.shaftThickness / 2);
        let start1: Point = start.add(startNorm);
        let start2: Point = start.subtract(startNorm);
        let end1: Point = end.add(startNorm);
        let end2: Point = end.subtract(startNorm);

        //figure out where the arrow head starts
        let headPnt: Point = fullVect.clone();
        headPnt.normalize(headPnt.length - arrowStyle.headLength);
        headPnt = headPnt.add(start);

        //calculate the arrowhead corners
        let headPntNorm: Point = startNorm.clone();
        headPntNorm.normalize(halfWidth);
        let edge1: Point = headPnt.add(headPntNorm);
        let edge2: Point = headPnt.subtract(headPntNorm);

        //Figure out where the arrow connects the the shaft, then calc the intersections
        let shaftCenter: Point = Point.interpolate(end, headPnt, arrowStyle.shaftPosition);
        let inter1: Point = GeomUtil.getLineIntersection(start1, end1, shaftCenter, edge1);
        let inter2: Point = GeomUtil.getLineIntersection(start2, end2, shaftCenter, edge2);

        // get controlpoints
        let intersect1Control: Point = GraphicsUtil.getCurveControlPoint(start1, inter1);
        let intersect2Control: Point = GraphicsUtil.getCurveControlPoint(start2, inter2);

        //Figure out the control points
        let edgeCenter: Point = Point.interpolate(end, headPnt, arrowStyle.edgeControlPosition);
        let edgeNorm: Point = startNorm.clone();
        edgeNorm.normalize(halfWidth * arrowStyle.edgeControlSize);
        let edgeCntrl1: Point = edgeCenter.add(edgeNorm);
        let edgeCntrl2: Point = edgeCenter.subtract(edgeNorm);

        graphics.moveTo(start1.x, start1.y);
        graphics.curveTo(intersect1Control.x, intersect1Control.y, inter1.x, inter1.y); // bezier one here
        graphics.lineTo(edge1.x, edge1.y);
        graphics.curveTo(edgeCntrl1.x, edgeCntrl1.y, end.x, end.y);
        graphics.curveTo(edgeCntrl2.x, edgeCntrl2.y, edge2.x, edge2.y);
        graphics.lineTo(inter2.x, inter2.y);
        graphics.curveTo(intersect2Control.x, intersect2Control.y, start2.x, start2.y); // bezier two here
        graphics.lineTo(start1.x, start1.y);

        let listOfPoints = [start1, inter1, edge1, /*edgeCntrl1,*/ end, /*edgeCntrl2*/, edge2, inter2, start2]
        return GraphicsUtil.getBoundsByPoints(listOfPoints);
    }

    /**
     * calculate the new coordinates for the point which is rotated against (0; 0)
     * @param point
     * @param angle
     */
    public static rotatePoint = (point: Point, angle: number): Point => {
        return new Point(
            point.x * Math.cos(angle) - point.y * Math.sin(angle),
            point.y * Math.cos(angle) + point.x * Math.sin(angle)
        )
    }

    /**
     * calculate the area bounds by given points
     * @param points
     * @returns {createjs.Rectangle}
     */
    public static getBoundsByPoints = (points: Array<Point>): createjs.Rectangle => {
        let xMin = Number.MAX_VALUE;
        let xMax = Number.MIN_VALUE;
        let yMin = Number.MAX_VALUE;
        let yMax = Number.MIN_VALUE;

        points.forEach((el:Point) => {
            xMin = xMin > el.x ? el.x : xMin;
            xMax = xMax < el.x ? el.x : xMax;
            yMin = yMin > el.y ? el.y : yMin;
            yMax = yMax < el.y ? el.y : yMax;
        })

        return new createjs.Rectangle(xMin, yMin, xMax - xMin, yMax - yMin)
    }

    // Controlpoint for Bezier-curve
    private static getCurveControlPoint(pixelStartPos: Point, pixelEndPos: Point): Point {
        let distance: number = Point.distance(pixelStartPos, pixelEndPos);
        let fullVect: Point = pixelEndPos.subtract(pixelStartPos);
        let normVect: Point = new Point(fullVect.y, -fullVect.x);
        normVect.normalize(distance * GraphicsUtil.BEZIER_LENGTH_TO_CURVE);
        let centerPoint: Point = Point.interpolate(pixelStartPos, pixelEndPos, .5);
        centerPoint = fullVect.x >= 0 ? centerPoint.add(normVect) : centerPoint.subtract(normVect);
        return centerPoint;
    }

    /**
     *
     * @param graphics
     * @param start
     * @param end
     * @param style
     *
     */
    public static drawArrowHead(graphics: Graphics, start: Point, end: Point, style: any = null): void {

        let arrowStyle: ArrowStyle;

        if (style == null) {
            arrowStyle = new ArrowStyle();
        }
        else if (style instanceof ArrowStyle) {
            arrowStyle = (<ArrowStyle>style);
        }
        else {
            arrowStyle = new ArrowStyle(style);
        }

        let vec: Point = end.subtract(start);
        let halfWidth: number = (arrowStyle.headWidth != -1) ? arrowStyle.headWidth / 2 : vec.length / 2;

        let shaft: Point = vec.clone();
        shaft.normalize(vec.length * arrowStyle.shaftPosition);
        shaft = start.add(shaft);

        //Make the vect a normal to the line
        vec = new Point(vec.y, -vec.x);

        let tmp: Point = vec.clone();
        tmp.normalize(arrowStyle.shaftControlSize * halfWidth);
        let shaftCenter: Point = Point.interpolate(start, shaft, arrowStyle.shaftControlPosition);
        let baseCntrl1: Point = shaftCenter.add(tmp);
        let baseCntrl2: Point = shaftCenter.subtract(tmp);

        vec.normalize(halfWidth);
        let base1: Point = start.add(vec);
        let base2: Point = start.subtract(vec);

        let edgeCenter: Point = Point.interpolate(start, end, arrowStyle.edgeControlPosition);
        vec.normalize(halfWidth * arrowStyle.edgeControlSize);
        let edgeCntrl1: Point = edgeCenter.add(vec);
        let edgeCntrl2: Point = edgeCenter.subtract(vec);

        //Draw the arrow
        graphics.moveTo(base1.x, base1.y);
        graphics.curveTo(baseCntrl1.x, baseCntrl1.y, shaft.x, shaft.y);
        graphics.curveTo(baseCntrl2.x, baseCntrl2.y, base2.x, base2.y);
        graphics.curveTo(edgeCntrl2.x, edgeCntrl2.y, end.x, end.y);
        graphics.curveTo(edgeCntrl1.x, edgeCntrl1.y, base1.x, base1.y);

    }

}


